"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { RichContent } from "@/components/rich-content";
import { cn } from "@/lib/utils";
import type { Locale } from "@/lib/i18n";
import { enhanceCodeBlocks } from "../lib/enhance-code-blocks";
import { hydrateWidgets } from "../lib/widgets";
import { hydrateHintLadders, type HintLadderOptions } from "../lib/hint-ladder";
import { RUN_LINK_VERSION_PARAM } from "@/features/playground/lib/run-link-version";
import { getLanguageBySlug } from "@/features/playground/config/languages";
import { STDIN_MAX_LENGTH } from "@/features/playground/types";
import { blockLanguageId } from "@/features/learn/reader/track-language";

/** Persisted per-user preference for bilingual lesson bodies. */
export const LESSON_LANG_STORAGE_KEY = "progsity:lesson-content-lang";

type ContentLang = "en" | "bn";

/**
 * What a program in the body says about itself, beyond its code. `problemKey`
 * is set when the code came from inside a `.pc-exercise` card ("Problem 7:
 * char-code" gives `char-code`), which is how the pane knows which problem a
 * submit is for (`LN-PRB01`); `languageVersion` is the version it was written
 * for when it is not the default (`LN-WEB02`, `runVersionOf`); `languageId` is
 * the Playground language it is in (`RV-12`, `runLanguageOf`); `stdin` is the
 * input its example was written for (`RV-13`, `programStdinIn`).
 */
export type LessonProgramMeta = {
  problemKey?: string;
  languageVersion?: string;
  languageId?: string;
  stdin?: string;
};

/**
 * `LN-RDR01`, docs/35 §5: what the Learn reader adds to a lesson body.
 *
 * All of it is optional, and the course engine's lesson player passes none of
 * it, which is the point: there is one renderer of a lesson body in this app and
 * the reader extends it rather than forking it (docs/35 §5, "built on
 * `LessonPlayerClient` and `LessonRichContent`, not beside them").
 */
type LearnExtras = {
  /** The hint ladder on every `.pc-exercise` that carries one (§5 item 3). */
  hintLadder: HintLadderOptions;
  /**
   * The inline checkpoint (§5 item 4), portalled into block 11's position:
   * after the problems and before "Common doubts" and "Takeaways"
   * (`STYLE_GUIDE.md` §3). Rendering it after the body would put it after the
   * closing lines, which is not where a checkpoint belongs.
   */
  checkpoint?: ReactNode;
  /**
   * Opens the Playground pane on a block's starter code (§5 item 1). Lesson
   * bodies carry `a.pc-run` links to `/playground/<lang>?code=<base64url>`; in
   * the reader the link feeds the pane instead of opening a tab, and without
   * this the links keep working as links. `LN-UI05` adds a Run button to the
   * header of each runnable code block (`run` below), which sends that block
   * and asks for it to run: `autoRun` is true for the button and false for a
   * link, whose code is a starter to edit. `meta` is what else the body knows
   * about the program (`LessonProgramMeta`).
   */
  onRunCode?: (code: string, autoRun: boolean, meta: LessonProgramMeta) => void;
  /** Which code blocks get a Run button, and its label (`LN-UI05`). */
  run?: { label: string; canRun: (lang: string | undefined, code: string) => boolean };
  /** Reported to the caller so its own copy can follow the body's language. */
  onLangChange?: (lang: ContentLang) => void;
  /**
   * `LN-UI05`: the reader owns the language and draws its own switch in the top
   * bar, so the body's toggle is not drawn and the body renders the language it
   * is given. That is also what puts Bangla in the server HTML of `/bn`: a
   * language chosen in an effect only arrives after hydration.
   */
  lang?: ContentLang;
  /** Called after the body's chrome is in place, with the body's root. */
  onEnhanced?: (root: HTMLElement) => void;
};

interface LessonRichContentProps {
  /** English lesson body (primary). */
  html?: string;
  /** Bangla adaptation of the lesson body. */
  htmlBn?: string;
  locale: Locale;
  className?: string;
  learn?: LearnExtras;
}

/** `?code=` carries base64url UTF-8; anything else is left to the link. */
export function decodeRunCode(href: string): string | null {
  try {
    const raw = new URL(href, window.location.origin).searchParams.get("code");
    if (!raw) return null;
    const bin = window.atob(raw.replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = Uint8Array.from(bin, (ch) => ch.charCodeAt(0));
    const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return text.trim() ? text : null;
  } catch {
    return null;
  }
}

/**
 * `LN-WEB02`: the version a program in the body asks for, as a contract version
 * id, or undefined for the language's default. A Run link names it in its own
 * `?version=` (`build-links.mjs` writes it); a code block names it with
 * `data-std` on its `<pre>` or `<code>`; and anything inside an "In C++20" box
 * (`.pc-cpp20`, `STYLE_GUIDE.md` §7) is C++20 even when nothing else says so,
 * which is the rule the content gates compile by. The pane checks the id
 * against the contract, so a stray value only falls back to the default.
 */
export function runVersionOf(element: Element, href?: string): string | undefined {
  if (href) {
    try {
      const named = new URL(href, window.location.origin).searchParams.get(RUN_LINK_VERSION_PARAM);
      if (named) return named;
    } catch {
      // A malformed href names no version.
    }
  }
  const pre = element.closest(".pc-codeblock")?.querySelector("pre");
  const own = pre?.getAttribute("data-std") ?? pre?.querySelector("code")?.getAttribute("data-std");
  if (own) return own;
  return element.closest(".pc-cpp20") ? "c++20" : undefined;
}

/**
 * `RV-12`: the Playground language a program in the body is in. A Run link
 * names it in its path (`/en/playground/c?code=…`, the Playground's own route);
 * a code block names it with its `language-*` class. An STL lesson carries C
 * programs beside its C++ ones, so the pane takes the language from the
 * program, and the track's language only when the program names none.
 */
export function runLanguageOf(element: Element, href?: string): string | undefined {
  if (href) {
    try {
      const slug = new URL(href, window.location.origin).pathname.match(/\/playground\/([^/]+)/)?.[1];
      return slug ? getLanguageBySlug(decodeURIComponent(slug))?.id : undefined;
    } catch {
      return undefined;
    }
  }
  const className = element.closest(".pc-codeblock")?.querySelector("pre code")?.className ?? "";
  return blockLanguageId(className.match(/language-([\w+#-]+)/)?.[1]);
}

/**
 * `RV-13`: the input a lesson program was written for. An example's block
 * carries it as `pre[data-stdin]` (the output under it is what that input
 * prints), and before this nothing read it, so Run on a program that reads
 * stdin printed a blank line. A Run link carries no input of its own: its
 * program is the block's, so the input is the block's whose code it is.
 */
export function programStdinIn(root: ParentNode, code: string): string | undefined {
  const wanted = code.trim();
  for (const pre of Array.from(root.querySelectorAll("pre[data-stdin]"))) {
    if ((pre.textContent ?? "").trim() === wanted) return stdinValue(pre.getAttribute("data-stdin"));
  }
  return undefined;
}

function stdinValue(raw: string | null): string | undefined {
  return raw ? raw.slice(0, STDIN_MAX_LENGTH) : undefined;
}

/** The problem key of the `.pc-exercise` card an element sits in, if any (`LN-PRB01`). */
export function exerciseKeyOf(element: Element): string | undefined {
  const heading = element.closest(".pc-exercise")?.querySelector(".pc-exercise-num")?.textContent ?? "";
  return heading.match(/problem\s*\d+\s*:\s*([a-z0-9-]+)/i)?.[1];
}

/**
 * Where the checkpoint goes: before "Common doubts", else before the
 * takeaways, else at the end. The mount point is created once and reused, so a
 * re-render does not leave a second one behind.
 */
function checkpointHost(root: HTMLElement): HTMLElement {
  const existing = root.querySelector<HTMLElement>("[data-pc-checkpoint]");
  if (existing) return existing;
  const host = document.createElement("div");
  host.dataset.pcCheckpoint = "true";
  const anchor = root.querySelector(".pc-doubts") ?? root.querySelector(".pc-takeaways");
  if (anchor) anchor.before(host);
  else root.append(host);
  return host;
}

export function LessonRichContent({
  html,
  htmlBn,
  locale,
  className,
  learn,
}: LessonRichContentProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ownLang, setLang] = useState<ContentLang>("en");
  const controlledLang = learn?.lang;
  const lang = controlledLang ?? ownLang;
  const [checkpointSlot, setCheckpointSlot] = useState<HTMLElement | null>(null);

  // Initial preference: sticky user choice first, site locale second.
  useEffect(() => {
    if (controlledLang) return;
    const stored = window.localStorage.getItem(LESSON_LANG_STORAGE_KEY);
    if (stored === "bn" || stored === "en") setLang(stored);
    else if (locale === "bn") setLang("bn");
  }, [controlledLang, locale]);

  const onLangChange = learn?.onLangChange;
  useEffect(() => {
    onLangChange?.(lang);
  }, [lang, onLangChange]);

  const selectLang = useCallback((next: ContentLang) => {
    setLang(next);
    try {
      window.localStorage.setItem(LESSON_LANG_STORAGE_KEY, next);
    } catch {
      // storage unavailable (private mode) - preference just won't persist
    }
  }, []);

  const hasEn = Boolean(html);
  const hasBn = Boolean(htmlBn);
  const showToggle = hasEn && hasBn && !controlledLang;
  const activeHtml = lang === "bn" && hasBn ? htmlBn! : (html ?? htmlBn ?? "");

  /**
   * The body element is memoised on the html itself, and that is not a
   * performance tweak: it is what keeps the injected chrome alive.
   *
   * `RichContent` renders `dangerouslySetInnerHTML`, and React re-applies it
   * whenever this component re-renders, which throws away every `data-pc-*`
   * mark, every code-block wrapper, every hint button and the checkpoint host
   * inside it. The effect below then sees un-enhanced nodes, enhances them
   * again, and calls `setCheckpointSlot` with the newly created host, which is
   * another re-render. That is an infinite loop, and on a real lesson it pinned
   * the main thread hard enough that Chrome offered to kill the page and no
   * button on the reader responded (`LN-UI02`, found on production 2026-09-20).
   *
   * Handing React the **same element object** for an unchanged body makes it
   * skip that subtree entirely, so the innerHTML is applied once per body.
   */
  const body = useMemo(() => <RichContent html={activeHtml} />, [activeHtml]);


  // The ladder's callbacks change on every render of the reader; an effect that
  // depended on them would tear down and rebuild the ladder mid-click, so the
  // live options are read through a ref and the effect depends on the body.
  const learnRef = useRef(learn);
  learnRef.current = learn;

  // The widgets' own words (`LN-WGT02`): the site's language, like the rest of
  // the chrome, while the widget's data is the body's.
  const widgetText = useTranslations("lessons.widgets");
  const widgetTextRef = useRef(widgetText);
  widgetTextRef.current = widgetText;
  // `RV-15`: the code block's Copy button in the site's language too; it was
  // English on /bn.
  const codeText = useTranslations("lessons.codeBlock");
  const codeTextRef = useRef(codeText);
  codeTextRef.current = codeText;

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const enhance = () => {
      const extras = learnRef.current;
      const text = codeTextRef.current;
      enhanceCodeBlocks(root, {
        ...(extras?.run ? { run: extras.run } : {}),
        labels: { copy: text("copy"), copied: text("copied"), copyCode: text("copyCode") },
      });
      hydrateWidgets(root, (key, values) => widgetTextRef.current(key, values));
      if (extras) {
        hydrateHintLadders(root, {
          ...extras.hintLadder,
          onOpen: (step) => learnRef.current?.hintLadder.onOpen(step),
        });
        setCheckpointSlot(extras.checkpoint ? checkpointHost(root) : null);
        extras.onEnhanced?.(root);
      }
    };
    enhance();
    // React can recreate the dangerouslySetInnerHTML subtree after this effect
    // has run (e.g. hydration repair), silently dropping the injected chrome.
    // Re-apply on any child mutation - enhanceCodeBlocks, hydrateWidgets and
    // hydrateHintLadders are idempotent via their data-* flags, so observed
    // re-runs are cheap no-ops.
    const observer = new MutationObserver(() => {
      if (
        root.querySelector("pre:not([data-pc-enhanced])") ||
        root.querySelector("[data-widget]:not([data-pc-widget-state])") ||
        root.querySelector(".pc-exercise:not([data-pc-ladder])")
      ) {
        enhance();
      }
    });
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [activeHtml]);

  // `a.pc-run` is a real link in the course player and a pane opener in the
  // reader, so the interception is a delegated listener rather than a rewrite
  // of the stored HTML: the link keeps its href and still works on middle click.
  useEffect(() => {
    const root = containerRef.current;
    if (!root || !learn?.onRunCode) return;
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      const runButton = target?.closest?.("button.pc-code-run");
      if (runButton) {
        const pre = runButton.closest(".pc-codeblock")?.querySelector("pre");
        const code = pre?.textContent ?? "";
        if (code.trim()) {
          learnRef.current?.onRunCode?.(code, true, {
            problemKey: exerciseKeyOf(runButton),
            languageVersion: runVersionOf(runButton),
            languageId: runLanguageOf(runButton),
            stdin: stdinValue(pre?.getAttribute("data-stdin") ?? null),
          });
        }
        return;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      const link = target?.closest?.("a.pc-run");
      if (!(link instanceof HTMLAnchorElement)) return;
      const code = decodeRunCode(link.href);
      if (!code) return;
      event.preventDefault();
      learnRef.current?.onRunCode?.(code, false, {
        problemKey: exerciseKeyOf(link),
        languageVersion: runVersionOf(link, link.href),
        languageId: runLanguageOf(link, link.href),
        stdin: programStdinIn(root, code),
      });
    };
    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [learn?.onRunCode]);

  if (!activeHtml) return null;

  return (
    <div ref={containerRef} className={className}>
      {showToggle && (
        <div className="mb-5 flex justify-end">
          <div
            role="group"
            aria-label="Lesson language"
            className="inline-flex items-center rounded-full border border-[rgb(var(--border))] bg-[rgb(var(--muted))] p-0.5"
          >
            <button
              type="button"
              onClick={() => selectLang("en")}
              aria-pressed={lang === "en"}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                lang === "en"
                  ? "bg-[rgb(var(--surface))] text-[rgb(var(--foreground))] shadow-sm"
                  : "text-[rgb(var(--muted-foreground))] hover:text-[rgb(var(--foreground))]",
              )}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => selectLang("bn")}
              aria-pressed={lang === "bn"}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                lang === "bn"
                  ? "bg-[rgb(var(--surface))] text-[rgb(var(--foreground))] shadow-sm"
                  : "text-[rgb(var(--muted-foreground))] hover:text-[rgb(var(--foreground))]",
              )}
            >
              বাংলা
            </button>
          </div>
        </div>
      )}
      {body}
      {checkpointSlot && learn?.checkpoint
        ? createPortal(learn.checkpoint, checkpointSlot)
        : null}
    </div>
  );
}
